<template>
  <div class="followUp">
    <div
      v-if="showNextSteps"
      class="nextStep"
    >
      <div class="title">
        Next step:
      </div>
      <Button
        class="upgradeButton"
        :data-test-id="$testId('upgradeButton')"
        :isDisabled="!hasBilling"
        :isLoading="false"
        :outline="false"
        text="Upgrade"
        type="primary"
        @click="handleUpgradeButtonClick"
      />
      <div
        v-if="!hasBilling"
        class="nobilling-message"
      >
        Contact a user with the <span class="role">Administrator with Billing</span>
        role to add this feature to your plan.
      </div>
      <div class="reachOutLink">
        Have Questions?

        <Button
          v-if="hasAccountManagerLink"
          :data-test-id="$testId('nextStepsReachOutButton')"
          :hasNoBackground="true"
          text="Reach out to us"
          @click="handleReachOutToUsButtonClick('next steps')"
        />
        <Link
          v-else
          :data-test-id="$testId('nextStepsReachOutLink')"
          :href="reachOutToUsLink.url"
          :isMatchText="false"
          isDarkText
          target="_blank"
          text="Reach out to us"
          @click.native="handleReachOutToUsLinkClick('next steps')"
        />
      </div>
      <div class="stopTrialLearnMore">
        *Learn to opt out of the trial in
        <Link
          :data-test-id="$testId('stopTrialLink')"
          :href="StopTrialKBLink.url"
          :isMatchText="false"
          target="_blank"
          :text="StopTrialKBLink.text"
          @click.native="handleStopTrialLinkClick('next steps')"
        />
      </div>
    </div>

    <div
      v-else
      class="moreResourcesSection"
    >
      <span>More resources:</span>
      <ul>
        <li>
          Learn more through
          <Link
            :data-test-id="$testId('gettingStartedLink')"
            :href="featureData.kbArticle"
            target="_blank"
            :text="`Getting started: ${featureData.featureName}`"
            @click.native="handleGettingStartedLinkClick"
          />
        </li>

        <li>
          Check out our
          <Link
            :data-test-id="$testId('discoverPageLink')"
            text="Discover Page."
            to="/discover"
            @click.native="handleDiscoverPageLinkClick"
          />
        </li>

        <li>
          Have Questions?

          <Button
            v-if="hasAccountManagerLink"
            :data-test-id="$testId('moreResourcesReachOutButton')"
            :hasNoBackground="true"
            text="Reach out to us"
            @click="handleReachOutToUsButtonClick('more resources')"
          />

          <Link
            v-else
            :data-test-id="$testId('moreResourcesReachOutLink')"
            :href="reachOutToUsLink.url"
            :isMatchText="false"
            isDarkText
            target="_blank"
            text="Reach out to us"
            @click.native="handleReachOutToUsLinkClick('more resources')"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { Button, Link } from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { mapGetters } from 'vuex';

import { trialModalLinks, trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import ModalEventBus from 'components/Modal/ModalEventBus';
import UpgradeModal from 'client/components/Modal/UpgradeModal.vue';

export default {
  name: 'FollowUp',

  components: {
    Button,
    Link,
  },

  ModalEventBus,

  props: {
    featureCode: {
      type: String,
      required: true,
    },
    showNextSteps: Boolean,
  },

  computed: {
    ...mapGetters('AccountManagerCalendarModule', [
      'getAccountManagerCalendarUrl',
    ]),

    ...mapGetters('SettingsModule', [
      'hasBilling',
    ]),

    featureData() {
      return trialPropsData[this.featureCode];
    },

    hasAccountManagerLink() {
      return !!this.getAccountManagerCalendarUrl;
    },

    reachOutToUsLink() {
      const url = this.hasAccountManagerLink
        ? this.getAccountManagerCalendarUrl
        : `${trialModalLinks.salesContact.url}?subject=${this.featureData.featurePackage} upgrade request`;

      return { url, text: trialModalLinks.salesContact.text };
    },
  },

  created() {
    this.StopTrialKBLink = trialModalLinks.stopATrial;
  },

  methods: {
    handleDiscoverPageLinkClick() {
      UiEvents.triggerLinkClicked({
        description: 'Discover Page Link clicked',
        page: `${this.featureData.featureName} Trial Modal`,
        section: 'more resources',
        toUrl: '#/discover',
        text: 'Discover Page',
      });

      this.$emit('close');
    },

    handleGettingStartedLinkClick() {
      UiEvents.triggerLinkClicked({
        page: `${this.featureData.featureName} Trial Modal`,
        section: 'more resources',
        description: `${this.featureData.featureName} getting started link clicked`,
        toUrl: this.featureData.kbArticle,
        text: `Getting started: ${this.featureData.featureName}`,
      });

      this.$emit('close');
    },

    handleReachOutToUsButtonClick(section) {
      UiEvents.triggerButtonClicked({
        page: `${this.featureData.featureName} Trial Modal`,
        description: 'Reach out to us button click',
        section,
        text: 'Reach out to us',
      });

      if (this.hasAccountManagerLink) {
        this.$emit('close');
        this.showUpgradeModal();
      }
    },

    handleReachOutToUsLinkClick(section) {
      UiEvents.triggerLinkClicked({
        page: `${this.featureData.featureName} Trial Modal`,
        toUrl: this.reachOutToUsLink.url,
        description: 'Reach out to us link click',
        section,
        text: 'Reach out to us',
      });

      if (this.hasAccountManagerLink) {
        this.$emit('close');
        this.showUpgradeModal();
      }
    },

    handleStopTrialLinkClick() {
      UiEvents.triggerLinkClicked({
        page: `${this.featureData.featureName} Trial Modal`,
        description: 'Stop Trial Link Click',
        section: 'next steps',
        toUrl: this.StopTrialKBLink.url,
        text: this.StopTrialKBLink.text,
      });

      this.$emit('close');
    },

    handleUpgradeButtonClick() {
      this.$router.push({ name: 'Upgrade' });

      UiEvents.triggerButtonClicked({
        page: `${this.featureData.featureName} Trial Modal`,
        description: 'Upgrade page link clicked',
        section: 'next steps',
        text: 'Upgrade Now',
        toUrl: '#/account/upgrade',
      });

      this.$emit('close');
    },

    showUpgradeModal() {
      this.$options.ModalEventBus.open({
        component: UpgradeModal,
        isLargeModal: true,
        componentProps: {
          calendarUrl: this.reachOutToUsLink.url,
          eventpage: this.eventLocation,
          packageInfo: this.featureData.featurePackage,
          showModal: true,
        },
      });
    },
  },
};
</script>

<style scoped>

.nextStep .title {
  font-size: var(--jcBody);
}

.moreResourcesSection {
  font-size: var(--jcBody);
}

.moreResourcesSection ul {
  padding-left: var(--jcBody);
}

.moreResourcesSection ul li {
  padding: var(--jc-spacer-small);
}

.nobilling-message {
  margin-top: var(--jc-spacer-medium);
}

.reachOutLink {
  margin-top: var(--jc-spacer-large);
}

.role {
  font-style: italic;
}

.stopTrialLearnMore {
  font-size: var(--jcBodyTiny);
  margin-top: var(--jc-spacer-small);
}

.upgradeButton {
  margin-top: var(--jc-spacer);
}
</style>
